import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import Loading from "../common/Loading";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { IApiClientError } from "../../shared/ApiClient";
import ButtonType from "../common/button/ButtonType";
import AnnexData from "../../model/AnnexData";
import TimeUtil from "../../utils/TimeUtil";
import AppIcons from "../../utils/AppIcons";
import ButtonInfo from "../common/button/ButtonInfo";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import { useAuth } from "../../contexts/AuthContext";
import ReportButton from "../common/ReportButton";

function AnnexDataDetails() {
  // reference to the AuthManager
  const authManager = useAuth();
  // get id param from the url
  const { id } = useParams();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<AnnexData>(AnnexData.EmptyInstance());
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();

  // load record data from the server
  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    const getAllData = async () => {
      try {
        let document = await apiClient?.get<AnnexData>({
          url: APIEndPoints.ANNEXDATA.ONE(parseInt(id!)),
        });
        console.log(document);
        setData(document);
      } catch (error: any) {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Something went wrong. Try again later!",
          });
        }
      } finally {
        setLoading(false);
      }
    };
    getAllData();
  }, [apiClient, id, alertsManager]);

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.ANNEXDATA.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Document record deleted successfully",
        });
        navigate(
          SETTINGS.get_path({
            section: AppSections.CHELEMENTS,
            id: data.id_chelement,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the document record",
          });
        }
      });
  };

  const handleDownload = async (id: number) => {
    console.log("ID:" + id);
    try {
      // Get the download code
      const responseData = await apiClient?.get({
        url: APIEndPoints.DOWNLOADS.CODE(id),
        requiresToken: true,
      });
      const downloadCode = (responseData as { download_code: string })
        .download_code;
      console.log("Download Code:", downloadCode);

      // `https://localhost/downloads/${downloadCode}`,
      // Force download by setting window location to the download URL
      window.location.href = SETTINGS.API_ROOT + `/downloads/${downloadCode}`;
      // print the url to test it
      // console.log("Download URL:", SETTINGS.API_ROOT + `/downloads/${downloadCode}`);
    } catch (error: any) {
      if (!error.processed) {
        alertsManager.showAlertMessage({
          type: AlertMessageType.ERROR,
          message: "Something went wrong. Try again later!",
        });
      }
    }
  };

  if (loading) {
    return <Loading />;
  }

  // const canEdit =
  //   !UserAgent.isChemiTool() &&
  //   (data.id_user === authManager.getUserID() ||
  //     authManager.getUserRole() === UserRoles.ADMIN);

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (data.id_user === authManager.getUserID() ||
      authManager.isAdmin() );

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Image */}
        {/*<div className="details-icon">
          <div>
            <img
              src={
                ImgUtil.display_icon(data.icon_path) ||
                process.env.REACT_APP_PUBLIC_URL +
                  "/resources/images/no-image.png"
              }
              alt="Document icon"
            />
          </div>
        </div>*/}

        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.title}</h1>
          </div>
          <div>
            {canEdit && (
              <Button
                label="Delete document"
                purpose={ButtonAction.DELETE}
                type={ButtonType.FILL_ICON_TEXT}
                onClick={() => {
                  alertsManager.showDialogWindow({
                    type: DialogWindowType.CONFIRM,
                    message: "Are you sure you want to delete this document?",
                    cancelLabel: "Cancel",
                    confirmLabel: "Yes, delete document",
                    onConfirm: handleDelete,
                  });
                }}
              />
            )}

            <ReportButton
                entityType="Annex Data Document"
                entityName={data.title}
                entityId={data.id || 0}
              />
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Document details</h2>
          {canEdit && (
            <Button
              label="Edit information"
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.ANNEXDATA,
                    id: data.id,
                    subsection: AppSubSections.EDIT,
                  }),
                )
              }
            />
          )}
        </div>
        <p className="details-description">{data.content}</p>
        <div className="ml-3 text-left">
          <ButtonInfo>
            {AppIcons.USER}
            created by: <span>{data.username}</span>
          </ButtonInfo>
          <ButtonInfo>
            {AppIcons.CLOCK}
            created on: {TimeUtil.display_date(data.timestamp)}
          </ButtonInfo>
        </div>
      </div>

      {/* Attachments section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Attachments ({data.docs ? data.docs.length : 0})</h2>
        </div>
        <div className="details-section-content">
          {data.docs && data.docs.length > 0 ? (
            <div className="details-attachments-grid">
              {data?.docs?.length &&
                data.docs.map((doc, index) => (
                  <div key={index} onClick={() => handleDownload(doc.id)}>
                    {AppIcons.getIconForFile(doc.name)}
                    <p>{doc.name || `Attachment ${index + 1}`}</p>
                  </div>
                ))}
            </div>
          ) : (
            <div className="details-no-records">No attachments available.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnnexDataDetails;
